import React, {
    Context,
    ReactNode,
    useCallback,
    useEffect,
    useState,
} from 'react';
import { useQuery } from '@apollo/client';
import _isEqual from 'lodash/isEqual';

import { queryStrings } from '@trqk/resolvers';
import { locale, removeNulls } from '@trqk/utils';
import {
    useGetTrqkUserSubscriptionSettingsLazyQuery,
    useGetChargebeePlanSettingsLazyQuery,
    useGetCustomerQrrLazyQuery,
    useGetChargebeeSubscriptionSettingsLazyQuery,
} from '@trqk/api';

import { useLogData, useToast, useDetectionTracks } from '..';

const { GET_SESSION_CURRENT_USER } = queryStrings;

const PAYMENT_STATUS_HANDLER_KEY = 'paymentStatus';

interface PaymentPlanStatusProviderProps {
    children: ReactNode;
    PaymentPlanStatusContext: Context<PaymentPlanStatusProviderValue>;
}

/**
 * Provides a {@link PaymentPlanStatusProviderValue}, which details of a user's current payment plan
 * status.
 * @remarks
 * `setSubscriptionDetails` is provided so that PaymentPlanStatusProvider can update the state
 * of `subscriptionDetails` when Chargebee's `success()` callback fires a
 * `getChargebeeSubscriptionSettings` query in:
 * packages/web/src/components/pages/Settings/components/PaymentPlan/PaymentPlan.tsx
 * This allows us to remove the CheckPaymentPlanStatus modal immediately (if appropriate) when a
 * user updates their plan.
 * @param props - see {@link PaymentPlanStatusProviderProps}
 */
export const PaymentPlanStatusProvider = ({
    children,
    PaymentPlanStatusContext,
}: PaymentPlanStatusProviderProps): JSX.Element => {
    const { addHandler, deleteHandlers } = useLogData();
    const { add: addToast } = useToast();
    const {
        detectionStats: {
            enabled: { nonDraftOnly: detectionTracksUsed },
        },
    } = useDetectionTracks();
    const currentUserQuery = useQuery(GET_SESSION_CURRENT_USER);
    const userId = currentUserQuery?.data?.session?.currentUser?.id ?? null;

    const [subscriptionSettings, setSubscriptionSettings] =
        useState<Maybe<PaymentPlanSubscriptionSettings>>(null);

    const [waitingForSettingsUpdate, setWaitingForSettingsUpdate] =
        useState(false);

    const [getTrqkUser, getTrqkUserQuery] =
        useGetTrqkUserSubscriptionSettingsLazyQuery({
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
            returnPartialData: true,
            onCompleted: data => {
                // `subscriptionSettings` can be sourced either from `getChargebeeSubscriptionSettings`
                // immediately following a successful checkout or from the `trqkUser`. We have to treat
                // each of these circumstances separately because of how our Chargebee integration works
                // in the backend. This is why the `onCompleted` method for both queries updates the
                // same state with `setSubscriptionSettings`.
                const nextTrqkUser = data?.getTrqkUser ?? null;

                if (nextTrqkUser !== null) {
                    const {
                        subscription_active,
                        subscription_detection_tracks,
                        subscription_expiration_date,
                        subscription_gap_analysis_bmat_sourced,
                        subscription_gap_analysis_enabled,
                        subscription_gap_analysis_trqk_sourced,
                        subscription_plan_type,
                        subscription_royalty_capacity,
                    } = nextTrqkUser;

                    const nextSubscriptionSettings: PaymentPlanSubscriptionSettings =
                        {
                            subscription_active,
                            subscription_detection_tracks,
                            subscription_expiration_date,
                            subscription_gap_analysis_bmat_sourced,
                            subscription_gap_analysis_enabled,
                            subscription_gap_analysis_trqk_sourced,
                            subscription_plan_type,
                            subscription_royalty_capacity,
                        };

                    if (subscriptionSettings === null) {
                        // On the first fetch, just set the `subscriptionSettings` state to the details
                        // from the trqkUser
                        setSubscriptionSettings(nextSubscriptionSettings);
                    } else if (
                        !_isEqual(
                            subscriptionSettings,
                            nextSubscriptionSettings,
                        )
                    ) {
                        // On subsequent fetches, only update the subscriptionSettings if they do not
                        // match what is on the trqkUser
                        setSubscriptionSettings(nextSubscriptionSettings);

                        // If we were waiting for an update after a successful checkout, show the user
                        // a toast to let them know it succeeded
                        if (waitingForSettingsUpdate) {
                            addToast({
                                kind: 'info-banner',
                                content:
                                    locale.enUS.ACCOUNT.SECTIONS.PAYMENT_PLAN
                                        .TOASTS.PAYMENT_PLAN_UPDATED,
                            });

                            setWaitingForSettingsUpdate(false);
                        }
                    }
                }
            },
        });

    const [getCustomerQrr, getCustomerQrrQuery] = useGetCustomerQrrLazyQuery({
        fetchPolicy: 'network-only',
    });

    const [getChargebeePlanSettings, chargebeePlanSettingsQuery] =
        useGetChargebeePlanSettingsLazyQuery({
            fetchPolicy: 'network-only',
        });

    const chargebeePlanSettings =
        chargebeePlanSettingsQuery?.data?.getChargebeePlanSettings ?? null;

    useEffect(() => {
        if (userId) {
            getTrqkUser({
                variables: {
                    id: userId,
                },
            });

            getCustomerQrr({
                variables: {
                    user_id: userId,
                },
            });

            // Even though we don't need to pass the user_id to `getChargebeePlanSettings`, we need
            // to wait until the user is logged in because the user_id is pulled from the access
            // token behind the scenes.
            getChargebeePlanSettings();
        }
    }, [userId]);

    const [getChargebeeSubscriptionSettings] =
        useGetChargebeeSubscriptionSettingsLazyQuery({
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
            onCompleted: (data): void => {
                const subscriptionSettingsData =
                    data?.getChargebeeSubscriptionSettings ?? null;

                if (subscriptionSettingsData !== null) {
                    const {
                        subscription_active,
                        subscription_detection_tracks,
                        subscription_expiration_date,
                        subscription_plan_type,
                        subscription_royalty_capacity,
                    } = subscriptionSettingsData;

                    // TODO: https://app.asana.com/0/1150917131612043/1201375140931903
                    // get these fields from getChargebeeSubscriptionSettings return value once we
                    // start to support Gap Analysis for Indie Edition users. At that point, the
                    // getChargebeeSubscriptionSettings return value should have the same fields
                    // that are now on the trqkUser:
                    //  - subscription_gap_analysis_bmat_sourced
                    //  - subscription_gap_analysis_enabled
                    //  - subscription_gap_analysis_trqk_sourced
                    const subscription_gap_analysis_bmat_sourced =
                        subscriptionSettings?.subscription_gap_analysis_bmat_sourced ??
                        null;
                    const subscription_gap_analysis_enabled =
                        subscriptionSettings?.subscription_gap_analysis_enabled ??
                        null;
                    const subscription_gap_analysis_trqk_sourced =
                        subscriptionSettings?.subscription_gap_analysis_trqk_sourced ??
                        null;

                    const nextSubscriptionSettings: PaymentPlanSubscriptionSettings =
                        {
                            subscription_active,
                            subscription_detection_tracks,
                            subscription_expiration_date,
                            subscription_gap_analysis_bmat_sourced,
                            subscription_gap_analysis_enabled,
                            subscription_gap_analysis_trqk_sourced,
                            subscription_plan_type,
                            subscription_royalty_capacity,
                        };

                    if (
                        !_isEqual(
                            subscriptionSettings,
                            nextSubscriptionSettings,
                        )
                    ) {
                        // Only update the subscriptionSettings if they are not the same
                        setSubscriptionSettings(nextSubscriptionSettings);

                        addToast({
                            kind: 'info-banner',
                            content:
                                locale.enUS.ACCOUNT.SECTIONS.PAYMENT_PLAN.TOASTS
                                    .PAYMENT_PLAN_UPDATED,
                        });
                    }
                } else {
                    setWaitingForSettingsUpdate(true);
                    addToast({
                        kind: 'info-banner',
                        content:
                            locale.enUS.ACCOUNT.SECTIONS.PAYMENT_PLAN.TOASTS
                                .PAYMENT_PLAN_IS_BEING_UPDATED,
                    });
                }
            },
        });

    const quarterlyRunRate = getCustomerQrrQuery?.data?.getCustomerQrr ?? null;

    /**
     * When any event happens that can update the Chargebee subscription settings, re-fetch the user
     * to update the data.
     */
    const handleChargebeeSubscriptionUpdate = useCallback(
        (event: LogEvent): void => {
            const status = event.eventStatus;

            if (
                status === 'success' &&
                getTrqkUserQuery &&
                getTrqkUserQuery.refetch &&
                userId !== null
            ) {
                getTrqkUserQuery.refetch({
                    id: userId,
                });
            }
        },
        [getTrqkUserQuery.refetch, userId],
    );

    // Add event handlers for the log events that relate to Chargebee subscription changes
    useEffect(() => {
        addHandler({
            eventTypes: [
                'chargebeeAdminOverride',
                'chargebeeSubscriptionCreated',
                'chargebeeSubscriptionChanged',
                'chargebeeSubscriptionCancelled',
                'chargebeeSubscriptionReactivated',
            ],
            handler: handleChargebeeSubscriptionUpdate,
            handlerKey: PAYMENT_STATUS_HANDLER_KEY,
        });

        return () => {
            // When PaymentPlanStatusProvider unmounts, remove its log event handlers
            deleteHandlers(PAYMENT_STATUS_HANDLER_KEY);
        };
    }, [handleChargebeeSubscriptionUpdate]);

    const handleEntityRefreshed = useCallback((): void => {
        if (getCustomerQrrQuery.refetch) {
            getCustomerQrrQuery.refetch();
        }
    }, [getCustomerQrrQuery.refetch]);

    // Add event handler for log events caused by deleting entities. This should cause the
    // quarterlyRunRate to update after an entity is deleted.
    useEffect(() => {
        addHandler({
            eventTypes: ['entityRefreshed'],
            handler: handleEntityRefreshed,
            handlerKey: PAYMENT_STATUS_HANDLER_KEY,
        });
    }, [handleEntityRefreshed]);

    const fetchChargebeeSubscriptionSettings = (
        currentUserId: string,
    ): void => {
        getChargebeeSubscriptionSettings({
            variables: {
                user_id: currentUserId,
            },
        });
    };

    const fetchTrqkUserSubscriptionSettings = useCallback(() => {
        if (getTrqkUserQuery && getTrqkUserQuery.refetch && userId !== null) {
            getTrqkUserQuery.refetch({
                id: userId,
            });
        }
    }, [getTrqkUserQuery, userId]);

    const subscriptionId =
        getTrqkUserQuery?.data?.getTrqkUser?.subscription_id ?? null;

    const planType = subscriptionSettings?.subscription_plan_type ?? null;

    const isPublisherEdition =
        planType === null ? null : planType === 'CHARGEBEE_PLAN_PUBLISHER';

    const detectionTracksCapacity =
        subscriptionSettings?.subscription_detection_tracks ?? null;

    const detectionTracksUsage: PaymentPlanUsageDetail = {
        capacity: detectionTracksCapacity,
        exceedsCapacity:
            detectionTracksCapacity !== null &&
            detectionTracksUsed !== null &&
            detectionTracksUsed > detectionTracksCapacity,
        used: detectionTracksUsed,
    };

    const royaltyCapacity =
        subscriptionSettings?.subscription_royalty_capacity ?? null;

    const royaltyUsage: PaymentPlanUsageDetail = {
        capacity: royaltyCapacity,
        exceedsCapacity:
            royaltyCapacity !== null &&
            quarterlyRunRate !== null &&
            quarterlyRunRate > royaltyCapacity,
        used: quarterlyRunRate,
    };

    const usage = {
        detectionTracks: detectionTracksUsage,
        exceedsCapacity:
            detectionTracksUsage.exceedsCapacity ||
            royaltyUsage.exceedsCapacity,
        royalties: royaltyUsage,
    };

    return (
        <PaymentPlanStatusContext.Provider
            value={{
                canUploadDetections:
                    (subscriptionSettings?.subscription_gap_analysis_enabled ===
                        true &&
                        subscriptionSettings?.subscription_gap_analysis_bmat_sourced ===
                            true) ??
                    false,
                chargebeePlanSettings: removeNulls(chargebeePlanSettings ?? []),
                isPublisherEdition,
                fetchChargebeeSubscriptionSettings,
                fetchTrqkUserSubscriptionSettings,
                setSubscriptionSettings,
                showFreeTrialFooter:
                    subscriptionSettings?.subscription_plan_type ===
                    'CHARGEBEE_PLAN_TRIAL',
                subscriptionId,
                subscriptionSettings,
                quarterlyRunRate,
                usage,
            }}
        >
            {children}
        </PaymentPlanStatusContext.Provider>
    );
};
